import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  DataPolicyAdminService,
  DataPolicyListItem,
  DataPolicyDetail,
  TablePolicyDetail,
  FieldDetail,
  NavigationDetail,
  ColumnMetadata,
  NavigationAliasOption,
  NavigationCardinality,
} from '../../../services/DataPolicyAdminService';
import { DialogService } from '../../utility/dialog/dialog-service';
import { ModalType } from '../../utility/dialog-template/dialog-template.component';

/**
 * Developer-only Data Policy Editor. Master-detail UI over the api/data-policy-admin authoring API.
 */
@Component({
  selector: 'app-data-policy-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './data-policy-editor.component.html',
  styleUrls: ['./data-policy-editor.component.css'],
})
export class DataPolicyEditorComponent implements OnChanges {
  @Input() clientId!: string;

  readonly Cardinality = NavigationCardinality;

  policies: DataPolicyListItem[] = [];
  selectedPolicyId: string | null = null;
  detail?: DataPolicyDetail;

  // Metadata caches for the dropdowns.
  schemas: string[] = [];
  private tablesBySchema = new Map<string, string[]>();
  private columnsByKey = new Map<string, ColumnMetadata[]>();
  private navAliasesByKey = new Map<string, NavigationAliasOption[]>();

  // ---- New-policy form ----
  creating = false;
  newPolicy = this.blankNewPolicy();

  // ---- Policy-header edit ----
  editingHeader = false;
  headerDraft = { name: '', canEdit: false };

  // ---- Add-table form ----
  addingTable = false;
  newTable = this.blankNewTable();

  // ---- Add-field form (scoped to one table at a time) ----
  addFieldForTableId: string | null = null;
  newField = this.blankNewField();

  // ---- Add-navigation form ----
  addingNav = false;
  newNav = this.blankNewNav();

  // ---- Drag-and-drop for field reordering ----
  dragTableId: string | null = null;
  dragFieldFromIndex = -1;
  dragOverTableId: string | null = null;
  dragOverFieldIndex = -1;

  constructor(
    private readonly service: DataPolicyAdminService,
    private readonly dialog: DialogService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && this.clientId) {
      this.resetState();
      this.loadPolicies();
      this.loadSchemas();
    }
  }

  // ---------------------------------------------------------------- state

  private resetState(): void {
    this.policies = [];
    this.selectedPolicyId = null;
    this.detail = undefined;
    this.creating = false;
    this.editingHeader = false;
    this.addingTable = false;
    this.addFieldForTableId = null;
    this.addingNav = false;
    this.newPolicy = this.blankNewPolicy();
  }

  private loadPolicies(preserveSelection = true): void {
    const previous = this.selectedPolicyId;
    this.service.list(this.clientId, policies => {
      this.policies = policies ?? [];
      if (preserveSelection && previous && this.policies.some(p => p.id === previous)) {
        this.loadDetail(previous);
      } else if (!this.policies.some(p => p.id === this.selectedPolicyId)) {
        this.selectedPolicyId = null;
        this.detail = undefined;
      }
    });
  }

  private loadSchemas(): void {
    this.service.getSchemas(schemas => (this.schemas = schemas ?? []));
  }

  selectPolicy(policy: DataPolicyListItem): void {
    if (this.selectedPolicyId === policy.id) return;
    this.editingHeader = false;
    this.addingTable = false;
    this.addFieldForTableId = null;
    this.addingNav = false;
    this.loadDetail(policy.id);
  }

  private loadDetail(policyId: string): void {
    this.selectedPolicyId = policyId;
    this.service.get(this.clientId, policyId, detail => {
      this.detail = detail;
      // Pre-load columns so display names are available immediately for all tables.
      detail.tables.forEach(t => this.ensureColumns(t.schemaName, t.tableName));
      // Pre-load nav aliases for existing navigations.
      detail.navigations.forEach(n =>
        this.ensureNavAliases(n.parentTablePolicyId, n.referenceTablePolicyId, n.cardinality)
      );
    });
  }

  // ---------------------------------------------------------------- metadata

  tablesFor(schema: string | null | undefined): string[] {
    if (!schema) return [];
    return this.tablesBySchema.get(schema) ?? [];
  }

  private ensureTables(schema: string): void {
    if (!schema || this.tablesBySchema.has(schema)) return;
    this.service.getTables(schema, tables => this.tablesBySchema.set(schema, tables ?? []));
  }

  columnsFor(schema: string, table: string): ColumnMetadata[] {
    return this.columnsByKey.get(`${schema}.${table}`) ?? [];
  }

  private ensureColumns(schema: string, table: string): void {
    const key = `${schema}.${table}`;
    if (this.columnsByKey.has(key)) return;
    this.service.getColumns(schema, table, cols => this.columnsByKey.set(key, cols ?? []));
  }

  // ---------------------------------------------------------------- create policy

  private blankNewPolicy() {
    return {
      name: '',
      canEdit: false,
      rootSchemaName: '',
      rootTableName: '',
      rootCanEditRecord: false,
      rootCanAddRecord: false,
      rootCanDeleteRecord: false,
    };
  }

  startCreate(): void {
    this.creating = true;
    this.newPolicy = this.blankNewPolicy();
  }

  cancelCreate(): void {
    this.creating = false;
  }

  onNewPolicySchemaChange(): void {
    this.newPolicy.rootTableName = '';
    this.ensureTables(this.newPolicy.rootSchemaName);
  }

  get newPolicyValid(): boolean {
    return !!this.newPolicy.name.trim()
      && !!this.newPolicy.rootSchemaName
      && !!this.newPolicy.rootTableName;
  }

  submitCreate(): void {
    if (!this.newPolicyValid) return;
    this.service.create(
      this.clientId,
      { ...this.newPolicy, name: this.newPolicy.name.trim() },
      id => {
        this.creating = false;
        this.selectedPolicyId = id ?? null;
        this.loadPolicies();
      }
    );
  }

  // ---------------------------------------------------------------- enable / disable

  toggleEnabled(policy: DataPolicyListItem, event?: Event): void {
    event?.stopPropagation();
    this.service.setEnabled(this.clientId, policy.id, !policy.isEnabled, () => this.loadPolicies());
  }

  // ---------------------------------------------------------------- policy header

  startEditHeader(): void {
    if (!this.detail) return;
    this.editingHeader = true;
    this.headerDraft = { name: this.detail.name, canEdit: this.detail.canEdit };
  }

  cancelEditHeader(): void {
    this.editingHeader = false;
  }

  saveHeader(): void {
    if (!this.detail || !this.headerDraft.name.trim()) return;
    this.service.updatePolicy(
      this.clientId,
      { id: this.detail.id, name: this.headerDraft.name.trim(), canEdit: this.headerDraft.canEdit },
      () => {
        this.editingHeader = false;
        this.loadPolicies();
      }
    );
  }

  // ---------------------------------------------------------------- tables

  private blankNewTable() {
    return {
      schemaName: '',
      tableName: '',
      canEditRecord: false,
      canAddRecord: false,
      canDeleteRecord: false,
    };
  }

  startAddTable(): void {
    this.addingTable = true;
    this.newTable = this.blankNewTable();
  }

  cancelAddTable(): void {
    this.addingTable = false;
  }

  onAddTableSchemaChange(): void {
    this.newTable.tableName = '';
    this.ensureTables(this.newTable.schemaName);
  }

  get newTableValid(): boolean {
    return !!this.newTable.schemaName && !!this.newTable.tableName;
  }

  submitAddTable(): void {
    if (!this.detail || !this.newTableValid) return;
    this.service.addTable(
      this.clientId,
      { dataPolicyId: this.detail.id, ...this.newTable },
      () => {
        this.addingTable = false;
        this.loadDetail(this.detail!.id);
      }
    );
  }

  saveTablePerms(table: TablePolicyDetail): void {
    this.service.updateTable(
      this.clientId,
      {
        tablePolicyId: table.id,
        canEditRecord: table.canEditRecord,
        canAddRecord: table.canAddRecord,
        canDeleteRecord: table.canDeleteRecord,
      },
      () => { }
    );
  }

  removeTable(table: TablePolicyDetail): void {
    if (!this.detail || table.isRoot) return;
    this.dialog.openModal(
      'Remove Table',
      `Remove "${table.schemaName}.${table.tableName}" from this policy? Any fields and navigations for this table will also be deleted.`,
      ModalType.QUESTION,
      () => this.service.removeTable(this.clientId, table.id, () => this.loadDetail(this.detail!.id)),
      null
    );
  }

  // ---------------------------------------------------------------- fields

  private blankNewField() {
    return { fieldNames: [] as string[], canEdit: false, sequence: 0 };
  }

  startAddField(table: TablePolicyDetail): void {
    this.addFieldForTableId = table.id;
    this.newField = this.blankNewField();
    this.newField.sequence = this.nextSequence(table.fields);
    this.ensureColumns(table.schemaName, table.tableName);
  }

  cancelAddField(): void {
    this.addFieldForTableId = null;
  }

  private nextSequence(items: { sequence: number }[]): number {
    return items.length ? Math.max(...items.map(i => i.sequence)) + 1 : 0;
  }

  /** Columns not yet added to this table, for the add-field checklist. */
  availableColumns(table: TablePolicyDetail): ColumnMetadata[] {
    const used = new Set(table.fields.map(f => f.fieldName));
    return this.columnsFor(table.schemaName, table.tableName).filter(c => !used.has(c.name));
  }

  /** Display name for an already-configured field, falling back to its raw column name. */
  fieldDisplayName(table: TablePolicyDetail, field: FieldDetail): string {
    const column = this.columnsFor(table.schemaName, table.tableName).find(c => c.name === field.fieldName);
    return column?.displayName || field.fieldName;
  }

  /** True when the display name differs from the raw field name. */
  fieldHasDisplayName(table: TablePolicyDetail, field: FieldDetail): boolean {
    const column = this.columnsFor(table.schemaName, table.tableName).find(c => c.name === field.fieldName);
    return !!(column?.displayName && column.displayName !== field.fieldName);
  }

  toggleFieldSelection(fieldName: string, checked: boolean): void {
    if (checked) {
      if (!this.newField.fieldNames.includes(fieldName)) this.newField.fieldNames.push(fieldName);
    } else {
      this.newField.fieldNames = this.newField.fieldNames.filter(f => f !== fieldName);
    }
  }

  get newFieldValid(): boolean {
    return this.newField.fieldNames.length > 0;
  }

  /**
   * Adds every selected field in a chain (single reload at the end).
   * Keeps the add-field panel open so the user can add more without reopening.
   */
  submitAddField(table: TablePolicyDetail): void {
    if (!this.detail || !this.newFieldValid) return;
    const tableId = table.id;
    const fieldNames = [...this.newField.fieldNames];
    const startSeq = this.newField.sequence;
    const canEdit = this.newField.canEdit;

    const addNext = (index: number): void => {
      if (index >= fieldNames.length) {
        // Keep the panel open — just clear selections and advance the sequence.
        this.newField.fieldNames = [];
        this.newField.sequence = startSeq + fieldNames.length;
        // addFieldForTableId intentionally NOT cleared so the panel stays visible.
        this.loadDetail(this.detail!.id);
        return;
      }
      this.service.addField(
        this.clientId,
        { tablePolicyId: tableId, fieldName: fieldNames[index], canEdit, sequence: startSeq + index },
        () => addNext(index + 1)
      );
    };
    addNext(0);
  }

  saveField(field: FieldDetail): void {
    this.service.updateField(
      this.clientId,
      { fieldId: field.id, canEdit: field.canEdit, sequence: field.sequence },
      () => { }
    );
  }

  removeField(table: TablePolicyDetail, field: FieldDetail): void {
    if (!this.detail) return;
    const label = this.fieldDisplayName(table, field);
    this.dialog.openModal(
      'Remove Field',
      `Remove "${label}" from ${table.schemaName}.${table.tableName}?`,
      ModalType.QUESTION,
      () => this.service.removeField(this.clientId, field.id, () => this.loadDetail(this.detail!.id)),
      null
    );
  }

  // ---------------------------------------------------------------- field drag-and-drop

  onFieldDragStart(e: DragEvent, table: TablePolicyDetail, index: number): void {
    this.dragTableId = table.id;
    this.dragFieldFromIndex = index;
    e.dataTransfer!.effectAllowed = 'move';
  }

  onFieldDragOver(e: DragEvent, table: TablePolicyDetail, index: number): void {
    if (this.dragTableId !== table.id) return;
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'move';
    this.dragOverTableId = table.id;
    this.dragOverFieldIndex = index;
  }

  onFieldDragLeave(e: DragEvent): void {
    // Only clear if leaving the row entirely (not hovering a child element).
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      this.dragOverTableId = null;
      this.dragOverFieldIndex = -1;
    }
  }

  onFieldDrop(e: DragEvent, table: TablePolicyDetail, dropIndex: number): void {
    e.preventDefault();
    this.dragOverTableId = null;
    this.dragOverFieldIndex = -1;

    if (this.dragTableId !== table.id || this.dragFieldFromIndex < 0 || this.dragFieldFromIndex === dropIndex) {
      this.dragTableId = null;
      this.dragFieldFromIndex = -1;
      return;
    }

    const fields = table.fields;
    const [moved] = fields.splice(this.dragFieldFromIndex, 1);
    fields.splice(dropIndex, 0, moved);

    // Normalise sequences to 0-based integers and persist.
    fields.forEach((f, i) => { f.sequence = i; });
    fields.forEach(f => this.saveField(f));

    this.dragTableId = null;
    this.dragFieldFromIndex = -1;
  }

  onFieldDragEnd(): void {
    this.dragTableId = null;
    this.dragFieldFromIndex = -1;
    this.dragOverTableId = null;
    this.dragOverFieldIndex = -1;
  }

  // ---------------------------------------------------------------- navigations

  private blankNewNav() {
    return {
      parentTablePolicyId: '',
      referenceSchema: '',
      referenceTable: '',
      linkAlias: '',
      cardinality: NavigationCardinality.OneToMany as number,
      sequence: 0,
    };
  }

  startAddNav(): void {
    if (!this.detail) return;
    this.addingNav = true;
    this.newNav = this.blankNewNav();
    this.newNav.sequence = this.nextSequence(this.detail.navigations);
  }

  cancelAddNav(): void {
    this.addingNav = false;
  }

  get newNavValid(): boolean {
    return !!this.newNav.parentTablePolicyId
      && !!this.newNav.referenceSchema
      && !!this.newNav.referenceTable
      && !!this.newNav.linkAlias
      && !(this.isNavSelfReference());
  }

  private isNavSelfReference(): boolean {
    const parent = this.detail?.tables.find(t => t.id === this.newNav.parentTablePolicyId);
    return !!parent
      && parent.schemaName === this.newNav.referenceSchema
      && parent.tableName === this.newNav.referenceTable;
  }

  /** When parent changes: pre-select the same schema for reference and load its tables. */
  onNewNavParentChange(): void {
    this.newNav.referenceSchema = '';
    this.newNav.referenceTable = '';
    this.newNav.linkAlias = '';
    if (!this.newNav.parentTablePolicyId) return;
    const parent = this.detail?.tables.find(t => t.id === this.newNav.parentTablePolicyId);
    if (parent) {
      this.newNav.referenceSchema = parent.schemaName;
      this.ensureTables(parent.schemaName);
    }
  }

  /** When reference schema changes: clear table and alias. */
  onNavRefSchemaChange(): void {
    this.newNav.referenceTable = '';
    this.newNav.linkAlias = '';
    this.ensureTables(this.newNav.referenceSchema);
  }

  /** When reference table or cardinality changes: refresh alias options. */
  onNavRefTableChange(): void {
    this.newNav.linkAlias = '';
    this.refreshNewNavAliases();
  }

  onNewNavCardinalityChange(): void {
    this.newNav.linkAlias = '';
    this.refreshNewNavAliases();
  }

  private refreshNewNavAliases(): void {
    if (!this.newNav.parentTablePolicyId || !this.newNav.referenceSchema || !this.newNav.referenceTable) return;
    const parent = this.detail?.tables.find(t => t.id === this.newNav.parentTablePolicyId);
    if (!parent) return;
    this.fetchNavAliases(
      parent.schemaName, parent.tableName,
      this.newNav.referenceSchema, this.newNav.referenceTable,
      this.newNav.cardinality,
      true
    );
  }

  newNavAliasOptions(): NavigationAliasOption[] {
    if (!this.newNav.parentTablePolicyId || !this.newNav.referenceSchema || !this.newNav.referenceTable) return [];
    const parent = this.detail?.tables.find(t => t.id === this.newNav.parentTablePolicyId);
    if (!parent) return [];
    return this.navAliasesByKey.get(
      this.navAliasSchemaKey(parent.schemaName, parent.tableName, this.newNav.referenceSchema, this.newNav.referenceTable, this.newNav.cardinality)
    ) ?? [];
  }

  /** Whether the nav's reference table is not yet in the policy (will be auto-added on submit). */
  get newNavRefNotInPolicy(): boolean {
    if (!this.detail || !this.newNav.referenceSchema || !this.newNav.referenceTable) return false;
    return !this.detail.tables.some(t =>
      t.schemaName === this.newNav.referenceSchema && t.tableName === this.newNav.referenceTable
    );
  }

  /**
   * Reference-table options for the nav form, split for optgroup rendering.
   * Tables already in the policy appear first (already available); metadata-only appear below.
   */
  navRefTablesInPolicy(): string[] {
    if (!this.newNav.referenceSchema || !this.detail) return [];
    const parent = this.detail.tables.find(t => t.id === this.newNav.parentTablePolicyId);
    return this.detail.tables
      .filter(t =>
        t.schemaName === this.newNav.referenceSchema &&
        (!parent || t.schemaName !== parent.schemaName || t.tableName !== parent.tableName)
      )
      .map(t => t.tableName);
  }

  navRefTablesFromMetadata(): string[] {
    if (!this.newNav.referenceSchema) return [];
    const inPolicySet = new Set(this.navRefTablesInPolicy());
    return this.tablesFor(this.newNav.referenceSchema).filter(t => !inPolicySet.has(t));
  }

  submitAddNav(): void {
    if (!this.detail || !this.newNavValid) return;
    const { parentTablePolicyId, referenceSchema, referenceTable, linkAlias, cardinality, sequence } = this.newNav;

    const existingRef = this.detail.tables.find(
      t => t.schemaName === referenceSchema && t.tableName === referenceTable
    );

    const doAddNav = (referenceTablePolicyId: string) => {
      this.service.addNavigation(
        this.clientId,
        { dataPolicyId: this.detail!.id, parentTablePolicyId, referenceTablePolicyId, linkAlias, cardinality, sequence },
        () => {
          this.addingNav = false;
          this.loadDetail(this.detail!.id);
        }
      );
    };

    if (existingRef) {
      doAddNav(existingRef.id);
    } else {
      // Reference table not yet in policy — add it with default (read-only) permissions first.
      this.service.addTable(
        this.clientId,
        { dataPolicyId: this.detail.id, schemaName: referenceSchema, tableName: referenceTable, canEditRecord: false, canAddRecord: false, canDeleteRecord: false },
        newTableId => doAddNav(newTableId)
      );
    }
  }

  /** Re-fetches Link Alias choices for an existing navigation after its cardinality changes. */
  onNavCardinalityChange(nav: NavigationDetail): void {
    nav.linkAlias = '';
    this.ensureNavAliases(nav.parentTablePolicyId, nav.referenceTablePolicyId, nav.cardinality, true);
  }

  saveNavigation(nav: NavigationDetail): void {
    if (!nav.linkAlias) return;
    this.service.updateNavigation(
      this.clientId,
      { navigationId: nav.id, linkAlias: nav.linkAlias, cardinality: nav.cardinality, sequence: nav.sequence },
      () => { }
    );
  }

  removeNavigation(nav: NavigationDetail): void {
    if (!this.detail) return;
    this.dialog.openModal(
      'Remove Navigation',
      `Remove navigation "${nav.linkAlias}" (${this.tableLabel(nav.parentTablePolicyId)} → ${this.tableLabel(nav.referenceTablePolicyId)})?`,
      ModalType.QUESTION,
      () => this.service.removeNavigation(this.clientId, nav.id, () => this.loadDetail(this.detail!.id)),
      null
    );
  }

  // ---------------------------------------------------------------- navigation aliases

  private navAliasSchemaKey(
    parentSchema: string, parentTable: string,
    refSchema: string, refTable: string,
    cardinality: number
  ): string {
    return `${parentSchema}.${parentTable}|${refSchema}.${refTable}|${cardinality}`;
  }

  private fetchNavAliases(
    parentSchema: string, parentTable: string,
    refSchema: string, refTable: string,
    cardinality: number,
    force = false
  ): void {
    const key = this.navAliasSchemaKey(parentSchema, parentTable, refSchema, refTable, cardinality);
    if (!force && this.navAliasesByKey.has(key)) return;
    this.service.getNavigationAliases(
      parentSchema, parentTable, refSchema, refTable, cardinality,
      options => this.navAliasesByKey.set(key, options ?? [])
    );
  }

  private ensureNavAliases(
    parentTablePolicyId: string,
    referenceTablePolicyId: string,
    cardinality: number,
    force = false
  ): void {
    if (!this.detail || !parentTablePolicyId || !referenceTablePolicyId) return;
    const parent = this.detail.tables.find(t => t.id === parentTablePolicyId);
    const reference = this.detail.tables.find(t => t.id === referenceTablePolicyId);
    if (!parent || !reference) return;
    this.fetchNavAliases(parent.schemaName, parent.tableName, reference.schemaName, reference.tableName, cardinality, force);
  }

  /** Alias options for an already-saved navigation row. */
  navAliasOptionsForNav(nav: NavigationDetail): NavigationAliasOption[] {
    if (!this.detail) return [];
    const parent = this.detail.tables.find(t => t.id === nav.parentTablePolicyId);
    const reference = this.detail.tables.find(t => t.id === nav.referenceTablePolicyId);
    if (!parent || !reference) return [];
    return this.navAliasesByKey.get(
      this.navAliasSchemaKey(parent.schemaName, parent.tableName, reference.schemaName, reference.tableName, nav.cardinality)
    ) ?? [];
  }

  // ---------------------------------------------------------------- helpers

  tableLabel(tablePolicyId: string): string {
    const t = this.detail?.tables.find(x => x.id === tablePolicyId);
    return t ? `${t.schemaName}.${t.tableName}` : '(unknown)';
  }

  cardinalityLabel(cardinality: number): string {
    return cardinality === NavigationCardinality.OneToOne ? 'One-to-One' : 'One-to-Many';
  }

  trackByPolicy = (_: number, p: DataPolicyListItem) => p.id;
  trackByTable = (_: number, t: TablePolicyDetail) => t.id;
  trackByField = (_: number, f: FieldDetail) => f.id;
  trackByNav = (_: number, n: NavigationDetail) => n.id;
}
